import { useQuery } from "@powersync/react";
import { useApp } from "@/app/AppContext";

// Dashboard reads over the local synced database. Team-scoped via maps.team_id.

export interface ActVisit { id: string; user_id: string | null; title: string | null; location_label: string | null; started_at: string | null; completed_at: string | null; obs: number }
export interface ActSign { id: string; field_note_id: string | null; approved_at: string; user_id: string | null; approver_name: string | null; location_label: string | null }
export interface ActTask { id: string; details: string | null; completed_at: string; completed_by_id: string | null; assigned_user_id: string | null; location_label: string | null; field_note_id: string | null }

// Everything since `since` (ISO); callers slice current vs previous period in JS.
export function useActivity(since: string) {
  const { teamId } = useApp();
  const visits = useQuery<ActVisit>(
    `SELECT fn.id, fn.user_id, fn.title, mf.label AS location_label, fn.started_at, fn.completed_at,
       (SELECT count(*) FROM mobile_field_note_media o WHERE o.field_note_id = fn.id) AS obs
     FROM mobile_field_notes fn JOIN map_features mf ON mf.id = fn.map_feature_id JOIN maps m ON m.id = mf.map_id
     WHERE m.team_id = ? AND fn.started_at >= ? ORDER BY fn.started_at DESC`, [teamId, since]);
  const signs = useQuery<ActSign>(
    `SELECT r.id, r.field_note_id, r.approved_at, fn.user_id, r.approver_name, mf.label AS location_label
     FROM mobile_custom_report_statuses r JOIN map_features mf ON mf.id = r.map_feature_id JOIN maps m ON m.id = mf.map_id
     LEFT JOIN mobile_field_notes fn ON fn.id = r.field_note_id
     WHERE m.team_id = ? AND r.approved_at >= ? ORDER BY r.approved_at DESC`, [teamId, since]);
  const tasks = useQuery<ActTask>(
    `SELECT r.id, r.details, r.completed_at, r.completed_by_id, r.assigned_user_id, mf.label AS location_label, r.field_note_id
     FROM mobile_reminders r LEFT JOIN map_features mf ON mf.id = r.map_feature_id LEFT JOIN maps m ON m.id = mf.map_id
     WHERE (m.team_id = ? OR r.map_feature_id IS NULL) AND r.completed_at >= ? ORDER BY r.completed_at DESC`, [teamId, since]);
  return { visits: visits.data, signs: signs.data, tasks: tasks.data, loading: visits.isLoading };
}

export interface Stage { id: string; name: string | null; colour: string | null }
export interface PipeFarm { id: string; label: string | null; status_id: string | null; responsible_user_id: string | null; description: string | null; last_visit: string | null; open_tasks: number; visits: number; updated_at: string | null }

export function usePipeline(topLevel: string) {
  const { teamId } = useApp();
  const stages = useQuery<Stage>("SELECT id, name, colour FROM map_feature_statuses WHERE team_id = ? ORDER BY created_at, name", [teamId]);
  const farms = useQuery<PipeFarm>(
    `SELECT mf.id, mf.label, mf.map_feature_status_id AS status_id, mf.responsible_user_id, mf.description, mf.updated_at,
       (SELECT max(coalesce(fn.completed_at, fn.started_at)) FROM mobile_field_notes fn WHERE fn.map_feature_id = mf.id
          OR fn.map_feature_id IN (SELECT member_id FROM mobile_zone_memberships WHERE zone_id = mf.id)) AS last_visit,
       (SELECT count(*) FROM mobile_field_notes fn WHERE fn.map_feature_id = mf.id
          OR fn.map_feature_id IN (SELECT member_id FROM mobile_zone_memberships WHERE zone_id = mf.id)) AS visits,
       (SELECT count(*) FROM mobile_reminders r WHERE r.completed_at IS NULL AND r.canceled_at IS NULL AND (r.map_feature_id = mf.id
          OR r.map_feature_id IN (SELECT member_id FROM mobile_zone_memberships WHERE zone_id = mf.id))) AS open_tasks
     FROM map_features mf JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ? AND mf.level = ? ORDER BY mf.label COLLATE NOCASE`, [teamId, topLevel]);
  return { stages: stages.data, farms: farms.data, loading: farms.isLoading };
}

export interface MetricType { id: string; name: string | null; data_type: string | null; n: number }
export interface Reading { id: string; value: string | null; created_at: string | null; metric_type_id: string; map_feature_id: string | null; field_note_id: string | null; location_label: string | null; top_id: string | null; top_label: string | null }

export function useMetricTypes() {
  const { teamId } = useApp();
  return useQuery<MetricType>(
    `SELECT mt.id, mt.name, mt.data_type, (SELECT count(*) FROM mobile_metrics x WHERE x.metric_type_id = mt.id) AS n
     FROM metric_types mt WHERE mt.team_id = ? ORDER BY n DESC, mt.name`, [teamId]);
}

// All readings since `since`, with the reading's location and its top-level ancestor.
export function useReadings(since: string) {
  const { teamId } = useApp();
  return useQuery<Reading>(
    `SELECT me.id, me.value, me.created_at, me.metric_type_id, me.map_feature_id, me.field_note_id, mf.label AS location_label,
       coalesce((SELECT z.zone_id FROM mobile_zone_memberships z WHERE z.member_id = mf.id LIMIT 1), mf.id) AS top_id,
       coalesce((SELECT p.label FROM mobile_zone_memberships z JOIN map_features p ON p.id = z.zone_id WHERE z.member_id = mf.id LIMIT 1), mf.label) AS top_label
     FROM mobile_metrics me JOIN map_features mf ON mf.id = me.map_feature_id JOIN maps m ON m.id = mf.map_id
     WHERE m.team_id = ? AND me.created_at >= ? AND coalesce(me.value_source, 0) <> 3 ORDER BY me.created_at`, [teamId, since]);
}
